import { BlobModel } from '@/models/blob';
import { signedApiClient } from '@/services/apiClient';

/**
 * Which of a blob's files to fetch. `original` is the captured original, the bytes exactly as the
 * device produced them, and `blob/:id` always means that. The other two are derivatives the worker
 * writes afterwards, each on its own route.
 */
type BlobVariant = 'original' | 'thumbnail' | 'display';

/** `IMG_0001.HEIC` saves as `IMG_0001 (display copy).jpg`, so it reads as derived from the photo. */
const displayCopyName = (name: string) => `${name.replace(/\.[^.]+$/, '')} (display copy).jpg`;

class BlobService {
  private blobEndpoint: string = '';
  initService(blobEndpoint: string) {
    this.blobEndpoint = blobEndpoint;
  }

  async patchBlob(identifier: string): Promise<BlobModel> {
    const uri = `${this.blobEndpoint}/${identifier}`;
    const apiClient = signedApiClient('PATCH', uri);
    return (await apiClient.patch(uri)).data;
  }

  async getBlobMetadata(identifier: string) {
    const uri = `${this.blobEndpoint}/${identifier}/metadata`;
    const apiClient = signedApiClient('GET', uri);
    const blob = await apiClient.get(uri);
    const blobModel =  BlobModel.mapFromBackend(blob.data);
    if (blobModel.hasThumbnail) {
      blobModel.thumbnailKey = await this.objectUrl(identifier, 'thumbnail', blobModel.name);
    }
    return blobModel;
  }

  /**
   * An object URL for a full-size view. Gives the display copy when the worker has written one,
   * because Chrome renders nothing from a HEIC captured original.
   */
  fullResolutionUrl(blob: BlobModel) {
    return this.objectUrl(blob.identifier, blob.hasDisplayCopy ? 'display' : 'original', blob.name);
  }

  /** Saves the captured original. */
  async downloadBlob(identifier: string, name?: string) {
    this.save(await this.fetchFile(identifier, 'original', name));
  }

  /** Saves the display copy, for the person who asked for a file they can open. */
  async downloadDisplayCopy(identifier: string, name: string) {
    this.save(await this.fetchFile(identifier, 'display', displayCopyName(name)));
  }

  private async objectUrl(identifier: string, variant: BlobVariant, name?: string) {
    return window.URL.createObjectURL(await this.fetchFile(identifier, variant, name));
  }

  private async fetchFile(identifier: string, variant: BlobVariant, name?: string) {
    const uri = variant === 'original' ?
      `${this.blobEndpoint}/${identifier}` :
      `${this.blobEndpoint}/${identifier}/${variant}`;
    const apiClient = signedApiClient('GET', uri);
    const blob = await apiClient.get<Blob>(uri, { responseType: 'blob' });
    const filename = name ? name : decodeURIComponent(
      blob.headers['content-disposition']
        .replace('attachment; filename=', '')
        .slice(1, -1)
    );
    const filetype = blob.headers['content-type'] as string;
    const systemBlob = new Blob([blob.data]);
    return new File([systemBlob], filename, { type: filetype });
  }

  private save(file: File) {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.name);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  }
}

export default new BlobService();
